
import { defineComponent } from 'vue';
import { ActionTypes } from '@/store/actionTypes';
import { MutationTypes } from '@/store/mutationTypes';
import AttachTrailerConfirmation from '@/components/AttachTrailerConfirmation.vue';
import LoadingModal from '@/components/LoadingModal.vue';
import ScanTrailer from '@/components/ScanTrailer.vue';
import TrailerPlateVinVerification from '@/components/TrailerPlateVinVerification.vue';
import IncorrectTrailerQrScanOverlay from '@/components/IncorrectTrailerQrScanOverlay.vue';
import useCameraPermission from '@/shared/composables/useCameraPermission';
import usePermissionAlert from '@/shared/composables/usePermissionAlert';
import Trailer from '@/types/Trailer';
import queries from '@/queries';
import FbaApiError from '@/types/FbaApiError';
import { TrailerSelectionMethod } from '@/types/TrailerSelectionMethod';
import { isNullOrUndefined } from '@/shared/helpers';

export default defineComponent({
    name: 'VerifiyProximity',
    components: {
        AttachTrailerConfirmation,
        LoadingModal,
        ScanTrailer,
        TrailerPlateVinVerification,
        IncorrectTrailerQrScanOverlay,
    },

    data: (): {
        showAttachConfirm: boolean;
        showLoading: boolean;
        showScanner: boolean;
        trailerToVerify: Trailer | undefined;
        isScannerPaused: boolean;
        isIncorrectTrailerScan: boolean;
    } => {
        return {
            showAttachConfirm: false,
            showLoading: false,
            showScanner: false,
            trailerToVerify: undefined,
            isIncorrectTrailerScan: false,
            isScannerPaused: false,
        };
    },
    props: {
        trailer: {
            type: Trailer,
            required: true,
        },
        distance: {
            type: Number,
            required: true,
        },
    },
    emits: ['onCloseVerification', 'plateOrVinVerified', 'qrScanVerified'],
    setup() {
        const {
            cameraPermission,
            isCameraPermissionGranted,
            isCameraPermissionPrompt,
            setHasAccessedCameraView,
            setIsCameraShared,
        } = useCameraPermission();
        const { setPermissionAlert } = usePermissionAlert();

        return {
            cameraPermission,
            isCameraPermissionGranted,
            isCameraPermissionPrompt,
            setHasAccessedCameraView,
            setPermissionAlert,
            setIsCameraShared,
        };
    },
    mounted() {
        this.setHasAccessedCameraView(true);
    },
    methods: {
        closeVerification() {
            this.$emit('onCloseVerification');
        },
        async enablePlateVin(): Promise<void> {
            this.showLoading = true;
            this.showScanner = false;

            if (
                isNullOrUndefined(this.trailerToVerify?.licensePlateNumber) ||
                isNullOrUndefined(
                    this.trailerToVerify?.vehicleIdentificationNumber
                )
            ) {
                const trailer = await queries.getTrailerById(
                    this.trailer.trailerNumber,
                    this.trailer.company
                );

                if (trailer instanceof Trailer) {
                    this.trailerToVerify = trailer;
                } else {
                    this.$store.commit(
                        MutationTypes.SET_ERROR,
                        trailer as FbaApiError
                    );
                    this.closeVerification();
                }
            }

            this.showAttachConfirm = true;
            this.showLoading = false;
        },
        isScannedTrailerSelectedTrailer(
            scannedTrailer: Trailer | FbaApiError
        ): boolean {
            if (scannedTrailer instanceof FbaApiError) {
                return false;
            }

            return (
                this.trailer?.trailerNumber === scannedTrailer?.trailerNumber &&
                this.trailer?.company === scannedTrailer?.company
            );
        },
        pauseScanner(): void {
            this.isScannerPaused = true;
        },
        verifiedPlateOrVin() {
            this.$emit('plateOrVinVerified');
        },
        async validateScannedTrailer(scannedQrId: string) {
            this.pauseScanner();
            const trailer = await queries.getTrailerByQrId(scannedQrId);

            if (
                trailer instanceof Trailer &&
                this.isScannedTrailerSelectedTrailer(trailer)
            ) {
                this.$store.dispatch(ActionTypes.SET_SELECTED_TRAILER, {
                    trailer,
                    trailerSelectionMethod: TrailerSelectionMethod.qrScan,
                });
                this.$emit('qrScanVerified');
            } else {
                this.isIncorrectTrailerScan = true;
            }
        },
    },
    watch: {
        cameraPermission: {
            async handler() {
                this.setHasAccessedCameraView(true);

                if (
                    this.isCameraPermissionGranted ||
                    this.isCameraPermissionPrompt
                ) {
                    if (this.isCameraPermissionGranted) {
                        this.setIsCameraShared(true);
                    }
                    this.showScanner = true;
                } else {
                    if (!isNullOrUndefined(this.cameraPermission)) {
                        this.setPermissionAlert();
                    }

                    await this.enablePlateVin();
                }
            },
            immediate: true,
        },
    },
});
